import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { languages } from '../mock';
import { Menu, X, ChevronDown, Globe } from 'lucide-react';

const Header = () => {
  const { language, changeLanguage, t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const handleLanguageSelect = (langCode) => {
    changeLanguage(langCode);
    setLangDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setLangDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedLanguage = languages.find(lang => lang.code === language);

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex items-center">
            <img 
              src="/images/Logo_0g_Baltics.png" 
              className="h-12 w-auto object-contain"
            />
            
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button onClick={() => scrollToSection('solutions')} className="text-gray-700 hover:text-[#2D4A8F] transition-colors font-medium">
              {t.nav.solutions}
            </button>
            <button onClick={() => scrollToSection('devices')} className="text-gray-700 hover:text-[#2D4A8F] transition-colors font-medium">
              {t.nav.devices}
            </button>
            <button onClick={() => scrollToSection('pilot')} className="text-gray-700 hover:text-[#2D4A8F] transition-colors font-medium">
              {t.nav.pilot}
            </button>
            <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-[#2D4A8F] transition-colors font-medium">
              {t.nav.contact}
            </button>
          </nav>

          {/* Language Dropdown */}
          <div className="hidden md:block relative" ref={dropdownRef}>
            <button
              onClick={() => setLangDropdownOpen(!langDropdownOpen)}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 hover:border-[#2D4A8F] transition-all bg-white"
            >
              <Globe size={18} style={{ color: '#2D4A8F' }} />
              <span className="font-semibold" style={{ color: '#2D4A8F' }}>
                {selectedLanguage?.label}
              </span>
              <ChevronDown 
                size={16} 
                className={`transition-transform duration-200 ${langDropdownOpen ? 'rotate-180' : ''}`}
                style={{ color: '#2D4A8F' }}
              />
            </button>

            {/* Dropdown Menu */}
            {langDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 animate-in fade-in slide-in-from-top-2 duration-200">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageSelect(lang.code)}
                    className={`w-full text-left px-4 py-2.5 hover:bg-[#F5F6F8] transition-colors flex items-center justify-between ${
                      language === lang.code ? 'bg-[#F5F6F8]' : ''
                    }`}
                  >
                    <span className={`font-medium ${
                      language === lang.code ? 'text-[#2D4A8F]' : 'text-gray-700'
                    }`}>
                      {lang.label}
                    </span>
                    {language === lang.code && (
                      <span className="text-[#2D4A8F]">✓</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t">
            <nav className="flex flex-col space-y-3">
              <button onClick={() => scrollToSection('solutions')} className="text-left px-4 py-2 text-gray-700 hover:bg-[#F5F6F8] rounded">
                {t.nav.solutions}
              </button>
              <button onClick={() => scrollToSection('devices')} className="text-left px-4 py-2 text-gray-700 hover:bg-[#F5F6F8] rounded">
                {t.nav.devices}
              </button>
              <button onClick={() => scrollToSection('pilot')} className="text-left px-4 py-2 text-gray-700 hover:bg-[#F5F6F8] rounded">
                {t.nav.pilot}
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-left px-4 py-2 text-gray-700 hover:bg-[#F5F6F8] rounded">
                {t.nav.contact}
              </button>
            </nav>
            
            {/* Mobile Language Selector */}
            <div className="mt-4 px-4">
              <div className="border-t pt-4">
                <p className="text-sm text-gray-600 mb-2 font-medium">Langue / Language</p>
                <div className="grid grid-cols-3 gap-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageSelect(lang.code)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        language === lang.code
                          ? 'bg-[#2D4A8F] text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-[#F5F6F8]'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
